// What the web client knows, in memory (docs/web-konzept.md, section 3.1). A version counter makes
// it observable for React (useSyncExternalStore); every change bumps it.

import { useSyncExternalStore } from "react";
import type { ConversationResponse, DocumentResponse, MessageResponse, TopicResponse } from "../api/contract.gen.ts";
import type { Content } from "./content.ts";

export interface StoredMessage {
  response: MessageResponse;
  mck?: Uint8Array<ArrayBuffer>;
  content: Content;
}

export interface Activity {
  user: string;
  what: string;
  /** Epoch ms after which it no longer shows. */
  until: number;
}

/** Someone else's recording in progress (docs/messenger-konzept.md, "Live mitlesen und mithören"). */
export interface LiveRecording {
  user: string;
  state: string;
  /** Their live transcript runs. */
  text: boolean;
  /** It can be listened to (their app sends Opus packets). */
  audio: boolean;
  turns: Map<number, { text: string; final: boolean }>;
  /** Epoch ms of the last frame; it disappears 12 s after. */
  at: number;
}

export class Store {
  readonly conversations = new Map<string, ConversationResponse>();
  /** Per conversation, by message id. */
  readonly messages = new Map<string, Map<string, StoredMessage>>();
  readonly topics = new Map<string, TopicResponse>();
  readonly documents = new Map<string, DocumentResponse>();
  /** The newest decrypted position of each live location share (by its message id). */
  readonly positions = new Map<string, { lat: number; lon: number; acc?: number; at: number }>();
  readonly online = new Set<string>();
  readonly lastSeen = new Map<string, number>();
  readonly activity = new Map<string, Activity[]>();
  /** Per conversation, by recorder. */
  readonly live = new Map<string, Map<string, LiveRecording>>();
  /** Conversations whose messages were loaded from GET …/messages (not only from sync). */
  readonly loaded = new Set<string>();
  me: string | null = null;
  private version = 0;
  private readonly listeners = new Set<() => void>();

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getVersion = () => this.version;

  changed() {
    this.version++;
    for (const listener of this.listeners) listener();
  }

  messagesOf(conversationId: string): StoredMessage[] {
    return [...(this.messages.get(conversationId)?.values() ?? [])].sort((a, b) => a.response.created_at.localeCompare(b.response.created_at));
  }

  message(conversationId: string, id: string): StoredMessage | undefined {
    return this.messages.get(conversationId)?.get(id);
  }

  putMessage(stored: StoredMessage) {
    const id = stored.response.conversation_id;
    let map = this.messages.get(id);
    if (!map) this.messages.set(id, (map = new Map()));
    // A later response without a key keeps the one known (the commit response has none).
    const old = map.get(stored.response.id);
    map.set(stored.response.id, { ...stored, mck: stored.mck ?? old?.mck });
  }

  removeConversation(id: string) {
    this.conversations.delete(id);
    this.messages.delete(id);
    this.loaded.delete(id);
    for (const [topicId, topic] of this.topics) if (topic.conversation_id === id) this.topics.delete(topicId);
  }

  clear() {
    this.conversations.clear();
    this.messages.clear();
    this.topics.clear();
    this.documents.clear();
    this.positions.clear();
    this.online.clear();
    this.lastSeen.clear();
    this.activity.clear();
    this.live.clear();
    this.loaded.clear();
    this.me = null;
    this.changed();
  }
}

export const store = new Store();

/** Re-renders when the store changes; [select] picks what the component reads. */
export function useStore<T>(select: (store: Store) => T): T {
  useSyncExternalStore(store.subscribe, store.getVersion);
  return select(store);
}
